import {
  createCompetition,
  deleteCompetition,
  enrollInPortalCompetition,
  withdrawFromPortalCompetition,
  getManageCompetitionEnrollmentWithdrawals,
  approveManageScoreSubmission,
  getManageCompetition,
  getManageCompetitionEnrollments,
  getManageCompetitionSubmissions,
  getManageCompetitionSubmission,
  getManageCompetitions,
  getPortalCompetition,
  getPortalCompetitionEnrollment,
  getPortalCompetitionRanking,
  getPortalCompetitions,
  getPortalCompletedCompetition,
  getPortalCompletedCompetitionRanking,
  getPortalCompetitionSessions,
  getPortalCompletedCompetitionSessions,
  getPortalCompetitionSessionRanking,
  getPortalCompletedCompetitionSessionRanking,
  getPortalCompletedCompetitions,
  deletePortalScoreSubmission,
  getPortalScoreSubmissions,
  rejectManageScoreSubmission,
  storePortalScoreSubmissionImage,
  submitPortalScoreSubmission,
  updateCompetition,
  addCompetitionManagers,
  resumeCompetition,
  stopCompetition,
  completeCompetition,
  duplicateCompetition,
  startCompetition,
  removeCompetitionManagers,
  getManageCompetitionSessions,
  getManageCompetitionSession,
  getManageCompetitionSessionRanking,
  createManageCompetitionSession,
  updateManageCompetitionSession,
  deleteManageCompetitionSession,
  startManageCompetitionSession,
  completeManageCompetitionSession,
  cancelManageCompetitionSession,
  getManageCompetitionSessionSeriesList,
  getManageCompetitionSessionSeries,
  createManageCompetitionSessionSeries,
  startManageCompetitionSessionSeries,
  completeManageCompetitionSessionSeries,
  addManageCompetitionSessionSeriesParticipant,
  updateManageCompetitionSessionSeriesParticipantWeaponGroup,
  removeManageCompetitionSessionSeriesParticipant,
  upsertManageCompetitionSessionSeriesParticipantScores,
} from '@api/competitionsApi';
import {
  ICreateCompetitionParams,
  IDuplicateCompetitionParams,
  ISubmitScoreSubmissionPayload,
  IStartCompetitionParams,
  IUpdateCompetitionParams,
  ManageCompetition,
  ManageCompetitionSession,
  IUpsertCompetitionSessionParams,
  ManageCompetitionSeries,
  ManageSeriesParticipant,
  IUpsertSeriesParticipantScoresParams,
  PortalCompetition,
  PortalCompetitionEnrollment,
  PortalCompetitionRanking,
  PortalCompetitionSession,
  PortalScoreSubmission,
} from '@api/competitionsApi/interfaces';
import { canCompleteSeries } from '@pages/Management/Competitions/Competitions/CompetitionSessionDetails/components/SeriesPanel/helpers';
import { IPaginatedReactQueryParams } from '../interfaces';
import {
  IPaginatedCompetitionEnrollmentWithdrawalsReactQueryParams,
  IPaginatedCompetitionEnrollmentsReactQueryParams,
  IPaginatedCompetitionSessionsReactQueryParams,
  IPaginatedCompetitionSubmissionsReactQueryParams,
} from './interfaces';
import { removeSubmissionFromManageSubmissionsCache } from './manageSubmissionsCache';
import { useMutation, useQuery, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { ManageCompetitionsSort } from '@api/competitionsApi/interfaces';
import { AxiosError } from 'axios';
import { ValidationErrors } from '@api/interfaces';

export const useGetManageCompetitions = ({
  page = 0,
  pageSize,
  sortField,
  search,
  enabled = true,
}: IPaginatedReactQueryParams<ManageCompetitionsSort>) => {
  return useQuery({
    queryKey: ['mng-competitions', page, pageSize, sortField, search],
    queryFn: () => {
      return getManageCompetitions(
        page + 1,
        pageSize === 'all' ? 0 : pageSize,
        sortField,
        search || undefined
      );
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: enabled === true,
  });
};

export const useGetManageCompetition = (slug?: string) => {
  return useQuery<ManageCompetition>({
    queryFn: () => getManageCompetition(slug || ''),
    queryKey: ['mng-competition', slug],
    enabled: Boolean(slug),
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      if ((error as AxiosError)?.response?.status === 404) {
        return false;
      }

      return failureCount < 3;
    },
  });
};

export const useCreateCompetitionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<string, AxiosError<ValidationErrors>, ICreateCompetitionParams>(
    {
      mutationFn: createCompetition,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['mng-competitions'],
        });
      },
    }
  );
};

export const useUpdateCompetitionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    string,
    AxiosError<ValidationErrors>,
    { slug: string; competitionData: IUpdateCompetitionParams }
  >({
    mutationFn: ({ slug, competitionData }) =>
      updateCompetition(slug, competitionData),
    onSuccess: (_slug, { slug }) => {
      queryClient.invalidateQueries({
        queryKey: ['mng-competitions'],
      });
      queryClient.invalidateQueries({
        queryKey: ['mng-competition', slug],
      });
    },
  });
};

export const useAddCompetitionManagersMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    AxiosError,
    { slug: string; memberIds: number[] }
  >({
    mutationFn: ({ slug, memberIds }) =>
      addCompetitionManagers(slug, memberIds).then(() => undefined),
    onSuccess: (_data, { slug }) => {
      queryClient.invalidateQueries({
        queryKey: ['mng-competition', slug],
      });
    },
  });
};

export const useRemoveCompetitionManagersMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    AxiosError,
    { slug: string; memberIds: number[] }
  >({
    mutationFn: ({ slug, memberIds }) =>
      removeCompetitionManagers(slug, memberIds).then(() => undefined),
    onSuccess: (_data, { slug }) => {
      queryClient.invalidateQueries({
        queryKey: ['mng-competition', slug],
      });
    },
  });
};

export const useStartCompetitionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      slug,
      ...params
    }: IStartCompetitionParams & { slug: string }) =>
      startCompetition(slug, params),
    onSuccess: (_data, { slug }) => {
      queryClient.invalidateQueries({
        queryKey: ['mng-competitions'],
      });
      queryClient.invalidateQueries({
        queryKey: ['mng-competition', slug],
      });
      queryClient.invalidateQueries({
        queryKey: ['portal-competitions'],
      });
      queryClient.invalidateQueries({
        queryKey: ['portal-competition', slug],
      });
    },
  });
};

const invalidateCompetitionLifecycleQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
  slug: string
): void => {
  queryClient.invalidateQueries({ queryKey: ['mng-competitions'] });
  queryClient.invalidateQueries({ queryKey: ['mng-competition', slug] });
  queryClient.invalidateQueries({ queryKey: ['mng-competition-submissions', slug] });
  queryClient.invalidateQueries({ queryKey: ['mng-competition-enrollments', slug] });
  queryClient.invalidateQueries({ queryKey: ['portal-competitions'] });
  queryClient.invalidateQueries({ queryKey: ['portal-competition', slug] });
  queryClient.invalidateQueries({ queryKey: ['portal-competition-enrollment', slug] });
  queryClient.invalidateQueries({ queryKey: ['portal-competition-submissions', slug] });
  queryClient.invalidateQueries({ queryKey: ['portal-competition-ranking', slug] });
};

export const useStopCompetitionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      slug,
      stopReason,
    }: {
      slug: string;
      stopReason: string;
    }) => stopCompetition(slug, stopReason),
    onSuccess: (_data, { slug }) => {
      invalidateCompetitionLifecycleQueries(queryClient, slug);
    },
  });
};

export const useDuplicateCompetitionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      slug,
      ...params
    }: { slug: string } & IDuplicateCompetitionParams) =>
      duplicateCompetition(slug, params),
    onSuccess: (newSlug, { slug: sourceSlug }) => {
      queryClient.invalidateQueries({ queryKey: ['mng-competitions'] });
      queryClient.invalidateQueries({ queryKey: ['mng-competition', sourceSlug] });
      queryClient.invalidateQueries({ queryKey: ['mng-competition', newSlug] });
    },
  });
};

export const useResumeCompetitionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      slug,
      ...params
    }: { slug: string } & Parameters<typeof resumeCompetition>[1]) =>
      resumeCompetition(slug, params),
    onSuccess: (_data, { slug }) => {
      invalidateCompetitionLifecycleQueries(queryClient, slug);
    },
  });
};

export const useCompleteCompetitionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      slug,
      completionMessage,
    }: {
      slug: string;
      completionMessage: string;
    }) => completeCompetition(slug, completionMessage),
    onSuccess: (_data, { slug }) => {
      queryClient.invalidateQueries({
        queryKey: ['mng-competitions'],
      });
      queryClient.invalidateQueries({
        queryKey: ['mng-competition', slug],
      });
      queryClient.invalidateQueries({
        queryKey: ['mng-competition-submissions', slug],
      });
      queryClient.invalidateQueries({
        queryKey: ['portal-competitions'],
      });
      queryClient.invalidateQueries({
        queryKey: ['portal-completed-competitions'],
      });
      queryClient.invalidateQueries({
        queryKey: ['portal-competition', slug],
      });
      queryClient.invalidateQueries({
        queryKey: ['portal-completed-competition', slug],
      });
      queryClient.invalidateQueries({
        queryKey: ['portal-competition-submissions', slug],
      });
      queryClient.invalidateQueries({
        queryKey: ['portal-competition-ranking', slug],
      });
      queryClient.invalidateQueries({
        queryKey: ['portal-completed-competition-ranking', slug],
      });
    },
  });
};

export const useDeleteCompetitionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteCompetition,
    onSuccess: (_data, slug) => {
      queryClient.invalidateQueries({
        queryKey: ['mng-competitions'],
      });
      queryClient.invalidateQueries({
        queryKey: ['mng-competition', slug],
      });
      queryClient.invalidateQueries({
        queryKey: ['portal-competitions'],
      });
      queryClient.invalidateQueries({
        queryKey: ['portal-competition', slug],
      });
    },
  });
};

export const useGetPortalCompetitions = ({
  perPage,
  enrolledOnly = false,
}: {
  perPage: number;
  enrolledOnly?: boolean;
}) => {
  return useInfiniteQuery({
    queryKey: ['portal-competitions', { perPage, enrolledOnly }],

    queryFn: ({ pageParam = 1 }: { pageParam: number }) => {
      return getPortalCompetitions(pageParam, perPage, { enrolledOnly });
    },

    initialPageParam: 1,

    getNextPageParam: (lastPage) => {
      if (lastPage.meta.currentPage >= lastPage.meta.lastPage) {
        return undefined;
      }

      return lastPage.meta.currentPage + 1;
    },

    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const useGetPortalCompletedCompetitions = ({
  perPage,
}: {
  perPage: number;
}) => {
  return useInfiniteQuery({
    queryKey: ['portal-completed-competitions', { perPage }],

    queryFn: ({ pageParam = 1 }: { pageParam: number }) => {
      return getPortalCompletedCompetitions(pageParam, perPage);
    },

    initialPageParam: 1,

    getNextPageParam: (lastPage) => {
      if (lastPage.meta.currentPage >= lastPage.meta.lastPage) {
        return undefined;
      }

      return lastPage.meta.currentPage + 1;
    },

    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const useGetPortalCompetition = (slug: string, enabled = true) => {
  return useQuery<PortalCompetition>({
    queryKey: ['portal-competition', slug],
    queryFn: () => getPortalCompetition(slug),
    enabled: Boolean(slug) && enabled,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      if ((error as AxiosError)?.response?.status === 404) {
        return false;
      }

      return failureCount < 3;
    },
  });
};

export const useGetPortalCompletedCompetition = (slug: string, enabled = true) => {
  return useQuery<PortalCompetition>({
    queryKey: ['portal-completed-competition', slug],
    queryFn: () => getPortalCompletedCompetition(slug),
    enabled: Boolean(slug) && enabled,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      if ((error as AxiosError)?.response?.status === 404) {
        return false;
      }

      return failureCount < 3;
    },
  });
};

export const useGetPortalCompetitionEnrollment = (slug: string) => {
  return useQuery<PortalCompetitionEnrollment>({
    queryKey: ['portal-competition-enrollment', slug],
    queryFn: () => getPortalCompetitionEnrollment(slug),
    enabled: Boolean(slug),
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const useEnrollInCompetitionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: enrollInPortalCompetition,
    onSuccess: (_data, slug) => {
      queryClient.invalidateQueries({
        queryKey: ['portal-competitions'],
      });
      queryClient.invalidateQueries({
        queryKey: ['portal-competitions-enrolled-count'],
      });
      queryClient.invalidateQueries({
        queryKey: ['portal-competition-enrollment', slug],
      });
      queryClient.invalidateQueries({
        queryKey: ['portal-competition', slug],
      });
      queryClient.invalidateQueries({
        queryKey: ['portal-competition-ranking', slug],
      });
      queryClient.invalidateQueries({
        queryKey: ['mng-competition', slug],
      });
      queryClient.invalidateQueries({
        queryKey: ['mng-competition-enrollments', slug],
      });
    },
  });
};

export const useWithdrawFromCompetitionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      slug,
      reason,
    }: {
      slug: string;
      reason?: string;
    }) => withdrawFromPortalCompetition(slug, reason),
    onSuccess: (_data, { slug }) => {
      queryClient.invalidateQueries({
        queryKey: ['portal-competitions'],
      });
      queryClient.invalidateQueries({
        queryKey: ['portal-competitions-enrolled-count'],
      });
      queryClient.invalidateQueries({
        queryKey: ['portal-competition-enrollment', slug],
      });
      queryClient.invalidateQueries({
        queryKey: ['portal-competition', slug],
      });
      queryClient.invalidateQueries({
        queryKey: ['portal-competition-ranking', slug],
      });
      queryClient.invalidateQueries({
        queryKey: ['portal-competition-submissions', slug],
      });
      queryClient.invalidateQueries({
        queryKey: ['mng-competition', slug],
      });
      queryClient.invalidateQueries({
        queryKey: ['mng-competition-enrollments', slug],
      });
      queryClient.invalidateQueries({
        queryKey: ['mng-competition-enrollment-withdrawals', slug],
      });
    },
  });
};

export const useGetCompetitionEnrollmentWithdrawals = ({
  slug,
  page = 0,
  pageSize,
  sortField,
  search,
}: IPaginatedCompetitionEnrollmentWithdrawalsReactQueryParams) => {
  return useQuery({
    queryKey: [
      'mng-competition-enrollment-withdrawals',
      slug,
      page,
      pageSize,
      sortField,
      search,
    ],
    queryFn: () => {
      return getManageCompetitionEnrollmentWithdrawals(
        slug,
        page + 1,
        pageSize === 'all' ? 0 : pageSize,
        sortField,
        search || undefined
      );
    },
    enabled: Boolean(slug),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const useGetCompetitionEnrollments = ({
  slug,
  page = 0,
  pageSize,
  sortField,
  search,
}: IPaginatedCompetitionEnrollmentsReactQueryParams) => {
  return useQuery({
    queryKey: [
      'mng-competition-enrollments',
      slug,
      page,
      pageSize,
      sortField,
      search,
    ],
    queryFn: () => {
      return getManageCompetitionEnrollments(
        slug,
        page + 1,
        pageSize === 'all' ? 0 : pageSize,
        sortField,
        search || undefined
      );
    },
    enabled: Boolean(slug),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const useGetPortalCompetitionRanking = (slug: string, enabled = true) => {
  return useQuery<PortalCompetitionRanking>({
    queryKey: ['portal-competition-ranking', slug],
    queryFn: () => getPortalCompetitionRanking(slug),
    enabled: Boolean(slug) && enabled,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      if ((error as AxiosError)?.response?.status === 404) {
        return false;
      }

      return failureCount < 3;
    },
  });
};

export const useGetPortalCompletedCompetitionRanking = (
  slug: string,
  enabled = true
) => {
  return useQuery<PortalCompetitionRanking>({
    queryKey: ['portal-completed-competition-ranking', slug],
    queryFn: () => getPortalCompletedCompetitionRanking(slug),
    enabled: Boolean(slug) && enabled,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      if ((error as AxiosError)?.response?.status === 404) {
        return false;
      }

      return failureCount < 3;
    },
  });
};

export type PortalCompetitionSessionsVariant = 'current' | 'results';

export const useGetPortalCompetitionSessions = (
  slug: string,
  variant: PortalCompetitionSessionsVariant,
  enabled = true
) => {
  return useQuery<PortalCompetitionSession[]>({
    queryKey: ['portal-competition-sessions', slug, variant],
    queryFn: () =>
      variant === 'results'
        ? getPortalCompletedCompetitionSessions(slug)
        : getPortalCompetitionSessions(slug),
    enabled: Boolean(slug) && enabled,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      if ((error as AxiosError)?.response?.status === 404) {
        return false;
      }

      return failureCount < 3;
    },
  });
};

export const useGetPortalCompetitionSessionRanking = (
  slug: string,
  sessionId: number | undefined,
  variant: PortalCompetitionSessionsVariant,
  enabled = true
) => {
  return useQuery<PortalCompetitionRanking>({
    queryKey: ['portal-competition-session-ranking', slug, sessionId, variant],
    queryFn: () =>
      variant === 'results'
        ? getPortalCompletedCompetitionSessionRanking(slug, sessionId || 0)
        : getPortalCompetitionSessionRanking(slug, sessionId || 0),
    enabled: Boolean(slug) && Boolean(sessionId) && enabled,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      if ((error as AxiosError)?.response?.status === 404) {
        return false;
      }

      return failureCount < 3;
    },
  });
};

export const useGetPortalScoreSubmissions = (slug: string) => {
  return useQuery<PortalScoreSubmission[]>({
    queryKey: ['portal-competition-submissions', slug],
    queryFn: () => getPortalScoreSubmissions(slug),
    enabled: Boolean(slug),
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

type SubmitScoreSubmissionVariables = {
  slug: string;
  image: File | Blob;
  filename: string;
  submission: ISubmitScoreSubmissionPayload;
};

export const useSubmitPortalScoreSubmissionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      slug,
      image,
      filename,
      submission,
    }: SubmitScoreSubmissionVariables) => {
      const scorecardImageId = await storePortalScoreSubmissionImage(
        slug,
        image,
        filename
      );

      return submitPortalScoreSubmission(slug, {
        ...submission,
        scorecardImageId,
      });
    },
    onSuccess: (_data, { slug }) => {
      queryClient.invalidateQueries({
        queryKey: ['portal-competition-submissions', slug],
      });
    },
  });
};

export const useDeletePortalScoreSubmissionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      slug,
      submissionId,
    }: {
      slug: string;
      submissionId: number;
    }) => deletePortalScoreSubmission(slug, submissionId),
    onSuccess: (_data, { slug, submissionId }) => {
      queryClient.setQueryData<PortalScoreSubmission[]>(
        ['portal-competition-submissions', slug],
        (old) => old?.filter((submission) => submission.id !== submissionId)
      );

      removeSubmissionFromManageSubmissionsCache(
        queryClient,
        slug,
        submissionId
      );

      queryClient.invalidateQueries({
        queryKey: ['portal-competition-submissions', slug],
      });
      queryClient.invalidateQueries({
        queryKey: ['mng-competition-submissions', slug],
      });
      queryClient.invalidateQueries({ queryKey: ['mng-competition', slug] });
    },
  });
};

export const buildManageCompetitionSubmissionsQueryKey = ({
  slug,
  page = 0,
  pageSize,
  sortField,
  search,
}: IPaginatedCompetitionSubmissionsReactQueryParams) => [
  'mng-competition-submissions',
  slug,
  page,
  pageSize,
  sortField,
  search,
];

export const useGetCompetitionSubmissions = ({
  slug,
  page = 0,
  pageSize,
  sortField,
  search,
}: IPaginatedCompetitionSubmissionsReactQueryParams) => {
  return useQuery({
    queryKey: buildManageCompetitionSubmissionsQueryKey({
      slug,
      page,
      pageSize,
      sortField,
      search,
    }),
    queryFn: () => {
      return getManageCompetitionSubmissions(
        slug,
        page + 1,
        pageSize === 'all' ? 0 : pageSize,
        sortField,
        search || undefined
      );
    },
    enabled: Boolean(slug),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const useGetManageCompetitionSubmission = (
  slug: string,
  submissionId: number | null
) => {
  return useQuery({
    queryKey: ['mng-competition-submission', slug, submissionId],
    queryFn: () => getManageCompetitionSubmission(slug, submissionId as number),
    enabled: Boolean(slug) && submissionId !== null,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const useApproveScoreSubmissionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      slug,
      submissionId,
    }: {
      slug: string;
      submissionId: number;
    }) => approveManageScoreSubmission(slug, submissionId),
    onSuccess: (_data, { slug }) => {
      queryClient.invalidateQueries({
        queryKey: ['mng-competition-submissions', slug],
      });
      queryClient.invalidateQueries({
        queryKey: ['mng-competition', slug],
      });
      queryClient.invalidateQueries({
        queryKey: ['portal-competition-submissions', slug],
      });
      queryClient.invalidateQueries({
        queryKey: ['portal-competition-ranking', slug],
      });
    },
  });
};

export const useRejectScoreSubmissionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: rejectManageScoreSubmission,
    onSuccess: (_data, { slug }) => {
      queryClient.invalidateQueries({
        queryKey: ['mng-competition-submissions', slug],
      });
      queryClient.invalidateQueries({
        queryKey: ['mng-competition', slug],
      });
      queryClient.invalidateQueries({
        queryKey: ['portal-competition-submissions', slug],
      });
      queryClient.invalidateQueries({
        queryKey: ['portal-competition-ranking', slug],
      });
    },
  });
};

export const useGetManageCompetitionSessions = ({
  slug,
  page = 0,
  pageSize,
  sortField,
  search,
  enabled = true,
}: IPaginatedCompetitionSessionsReactQueryParams) => {
  return useQuery({
    queryKey: [
      'mng-competition-sessions',
      slug,
      page,
      pageSize,
      sortField,
      search,
    ],
    queryFn: () =>
      getManageCompetitionSessions(
        slug,
        page + 1,
        pageSize === 'all' ? 0 : pageSize,
        sortField,
        search || undefined
      ),
    enabled: Boolean(slug) && enabled,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const useGetManageCompetitionSession = (
  slug?: string,
  sessionId?: number
) => {
  return useQuery<ManageCompetitionSession>({
    queryKey: ['mng-competition-session', slug, sessionId],
    queryFn: () => getManageCompetitionSession(slug || '', sessionId || 0),
    enabled: Boolean(slug) && Boolean(sessionId),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: (failureCount, error) => {
      if ((error as AxiosError)?.response?.status === 404) {
        return false;
      }

      return failureCount < 3;
    },
  });
};

export const useCreateCompetitionSessionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ManageCompetitionSession,
    AxiosError<ValidationErrors>,
    { slug: string; sessionData: IUpsertCompetitionSessionParams }
  >({
    mutationFn: ({ slug, sessionData }) =>
      createManageCompetitionSession(slug, sessionData),
    onSuccess: (_data, { slug }) => {
      queryClient.invalidateQueries({
        queryKey: ['mng-competition-sessions', slug],
      });
      queryClient.invalidateQueries({
        queryKey: ['mng-competition', slug],
      });
    },
  });
};

export const useUpdateCompetitionSessionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ManageCompetitionSession,
    AxiosError<ValidationErrors>,
    {
      slug: string;
      sessionId: number;
      sessionData: IUpsertCompetitionSessionParams;
    }
  >({
    mutationFn: ({ slug, sessionId, sessionData }) =>
      updateManageCompetitionSession({
        slug,
        sessionId,
        ...sessionData,
      }),
    onSuccess: (_data, { slug }) => {
      queryClient.invalidateQueries({
        queryKey: ['mng-competition-sessions', slug],
      });
      queryClient.invalidateQueries({
        queryKey: ['mng-competition', slug],
      });
    },
  });
};

export const useDeleteCompetitionSessionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    AxiosError,
    { slug: string; sessionId: number }
  >({
    mutationFn: ({ slug, sessionId }) =>
      deleteManageCompetitionSession({ slug, sessionId }).then(() => undefined),
    onSuccess: (_data, { slug }) => {
      queryClient.invalidateQueries({
        queryKey: ['mng-competition-sessions', slug],
      });
      queryClient.invalidateQueries({
        queryKey: ['mng-competition', slug],
      });
    },
  });
};

export const useStartCompetitionSessionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ManageCompetitionSession,
    AxiosError,
    { slug: string; sessionId: number }
  >({
    mutationFn: startManageCompetitionSession,
    onSuccess: (_data, { slug, sessionId }) => {
      queryClient.invalidateQueries({
        queryKey: ['mng-competition-sessions', slug],
      });
      queryClient.invalidateQueries({
        queryKey: ['mng-competition-session', slug, sessionId],
      });
    },
  });
};

export const useCancelCompetitionSessionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ManageCompetitionSession,
    AxiosError<ValidationErrors>,
    {
      slug: string;
      sessionId: number;
      cancellationReason: string;
    }
  >({
    mutationFn: cancelManageCompetitionSession,
    onSuccess: (_data, { slug, sessionId }) => {
      queryClient.invalidateQueries({
        queryKey: ['mng-competition-sessions', slug],
      });
      queryClient.invalidateQueries({
        queryKey: ['mng-competition-session', slug, sessionId],
      });
      queryClient.invalidateQueries({
        queryKey: ['mng-competition', slug],
      });
    },
  });
};

export const useCompleteCompetitionSessionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ManageCompetitionSession,
    AxiosError,
    { slug: string; sessionId: number }
  >({
    mutationFn: completeManageCompetitionSession,
    onSuccess: (_data, { slug, sessionId }) => {
      queryClient.invalidateQueries({
        queryKey: ['mng-competition-sessions', slug],
      });
      queryClient.invalidateQueries({
        queryKey: ['mng-competition-session', slug, sessionId],
      });
    },
  });
};

const invalidateSessionSeriesQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
  slug: string,
  sessionId: number,
  seriesId?: number
): void => {
  queryClient.invalidateQueries({
    queryKey: ['mng-competition-session-series', slug, sessionId],
  });
  queryClient.invalidateQueries({
    queryKey: ['mng-competition-session', slug, sessionId],
  });
  queryClient.invalidateQueries({
    queryKey: ['mng-competition-sessions', slug],
  });
  queryClient.invalidateQueries({
    queryKey: ['mng-competition-session-ranking', slug, sessionId],
  });

  if (seriesId !== undefined) {
    queryClient.invalidateQueries({
      queryKey: ['mng-competition-session-series-detail', slug, sessionId, seriesId],
    });
  }
};

export const useGetManageCompetitionSessionRanking = (
  slug?: string,
  sessionId?: number,
  enabled = true
) => {
  return useQuery<PortalCompetitionRanking>({
    queryKey: ['mng-competition-session-ranking', slug, sessionId],
    queryFn: () =>
      getManageCompetitionSessionRanking(slug || '', sessionId || 0),
    enabled: Boolean(slug) && Boolean(sessionId) && enabled,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const useGetManageCompetitionSessionSeriesList = (
  slug?: string,
  sessionId?: number,
  enabled = true
) => {
  return useQuery<ManageCompetitionSeries[]>({
    queryKey: ['mng-competition-session-series', slug, sessionId],
    queryFn: () =>
      getManageCompetitionSessionSeriesList(slug || '', sessionId || 0),
    enabled: Boolean(slug) && Boolean(sessionId) && enabled,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const useGetManageCompetitionSessionSeries = (
  slug?: string,
  sessionId?: number,
  seriesId?: number,
  enabled = true
) => {
  return useQuery<ManageCompetitionSeries>({
    queryKey: ['mng-competition-session-series-detail', slug, sessionId, seriesId],
    queryFn: () =>
      getManageCompetitionSessionSeries(slug || '', sessionId || 0, seriesId || 0),
    enabled: Boolean(slug) && Boolean(sessionId) && Boolean(seriesId) && enabled,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const useCreateCompetitionSessionSeriesMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ManageCompetitionSeries,
    AxiosError<ValidationErrors>,
    { slug: string; sessionId: number }
  >({
    mutationFn: createManageCompetitionSessionSeries,
    onSuccess: (_data, { slug, sessionId }) => {
      invalidateSessionSeriesQueries(queryClient, slug, sessionId);
    },
  });
};

export const useStartCompetitionSessionSeriesMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ManageCompetitionSeries,
    AxiosError,
    { slug: string; sessionId: number; seriesId: number }
  >({
    mutationFn: startManageCompetitionSessionSeries,
    onSuccess: (_data, { slug, sessionId, seriesId }) => {
      invalidateSessionSeriesQueries(queryClient, slug, sessionId, seriesId);
    },
  });
};

export const useCompleteCompetitionSessionSeriesMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ManageCompetitionSeries,
    AxiosError,
    { slug: string; sessionId: number; seriesId: number }
  >({
    mutationFn: completeManageCompetitionSessionSeries,
    onSuccess: (_data, { slug, sessionId, seriesId }) => {
      invalidateSessionSeriesQueries(queryClient, slug, sessionId, seriesId);
    },
  });
};

export const useAddCompetitionSessionSeriesParticipantMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ManageSeriesParticipant,
    AxiosError<ValidationErrors>,
    {
      slug: string;
      sessionId: number;
      seriesId: number;
      memberId: number;
      weaponGroup?: string;
    }
  >({
    mutationFn: addManageCompetitionSessionSeriesParticipant,
    onSuccess: (_data, { slug, sessionId, seriesId }) => {
      invalidateSessionSeriesQueries(queryClient, slug, sessionId, seriesId);
    },
  });
};

export const useUpdateCompetitionSessionSeriesParticipantWeaponGroupMutation =
  () => {
    const queryClient = useQueryClient();

    return useMutation<
      ManageSeriesParticipant,
      AxiosError<ValidationErrors>,
      {
        slug: string;
        sessionId: number;
        seriesId: number;
        participantId: number;
        weaponGroup: string;
      }
    >({
      mutationFn: updateManageCompetitionSessionSeriesParticipantWeaponGroup,
      onSuccess: (_data, { slug, sessionId, seriesId }) => {
        invalidateSessionSeriesQueries(queryClient, slug, sessionId, seriesId);
      },
    });
  };

export const useRemoveCompetitionSessionSeriesParticipantMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    AxiosError,
    { slug: string; sessionId: number; seriesId: number; participantId: number }
  >({
    mutationFn: ({ slug, sessionId, seriesId, participantId }) =>
      removeManageCompetitionSessionSeriesParticipant({
        slug,
        sessionId,
        seriesId,
        participantId,
      }).then(() => undefined),
    onSuccess: (_data, { slug, sessionId, seriesId }) => {
      invalidateSessionSeriesQueries(queryClient, slug, sessionId, seriesId);
    },
  });
};

export const useUpsertCompetitionSessionSeriesScoresMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    ManageSeriesParticipant,
    AxiosError<ValidationErrors>,
    {
      slug: string;
      sessionId: number;
      seriesId: number;
      participantId: number;
      scores: IUpsertSeriesParticipantScoresParams['scores'];
      partIds: number[];
    }
  >({
    mutationFn: upsertManageCompetitionSessionSeriesParticipantScores,
    onSuccess: (
      updatedParticipant,
      { slug, sessionId, seriesId, participantId, partIds }
    ) => {
      queryClient.setQueryData<ManageCompetitionSeries>(
        ['mng-competition-session-series-detail', slug, sessionId, seriesId],
        (currentSeries) => {
          if (!currentSeries?.participants) {
            return currentSeries;
          }

          const participants = currentSeries.participants.map((participant) =>
            participant.id === participantId ? updatedParticipant : participant
          );

          return {
            ...currentSeries,
            participants,
            canComplete:
              currentSeries.status === 'in_progress' &&
              canCompleteSeries(participants, partIds),
          };
        }
      );
    },
  });
};
